/**
 * File: useBooksFrameController.ts
 * Path: apps/admin/src/shared/hooks/useBooksFrameController.ts
 * Description: What the Accounting pages' book picker, "set up these books"
 * step and books frame need: the open books, the switch, and the set-up form.
 */
import { useState } from "react";
import { booksToday, defaultBooksStart, DEFAULT_YEAR_END_MONTH, type ProfileKind } from "@idk/config";
import { useBooksChoice, useBooksKind, useIsBooksOwner, useOpenBooks } from "./useBooksQueries";
import { useBooksCommand } from "./useBooksMutations";

const SWITCH: { kind: ProfileKind; label: string }[] = [
  { kind: "company", label: "IDK Solutions (Pty) Ltd" },
  { kind: "personal", label: "My own books" },
];

export const useBooksPickerController = () => {
  // Just the remembered choice: the sidebar must not load the books on every page.
  const kind = useBooksKind();
  const setKind = useBooksChoice((s) => s.setKind);
  const options = useIsBooksOwner() ? SWITCH : SWITCH.filter((o) => o.kind === "company");
  const current = options.find((o) => o.kind === kind) ?? options[0];
  return { kind, setKind, options, current };
};

export const useSetUpBooksController = (kind: ProfileKind, profileId: string) => {
  const command = useBooksCommand(profileId);
  const [booksStart, setBooksStart] = useState(() => defaultBooksStart(booksToday()));
  return {
    booksStart,
    setBooksStart,
    pending: command.isPending,
    canSubmit: !command.isPending && booksStart.endsWith("-01"),
    submit: () =>
      command.mutate({
        action: "setupProfile",
        kind,
        booksStart,
        yearEndMonth: DEFAULT_YEAR_END_MONTH,
      }),
  };
};

export const useBooksFrameController = () => {
  const { kind, profileId, profile } = useOpenBooks();
  return {
    kind,
    profileId,
    loading: profile.isLoading,
    failed: Boolean(profile.error),
    data: profile.data,
    retrying: profile.isFetching,
    retry: () => void profile.refetch(),
  };
};
